// Lightweight icon grid to mock how a project-type-driven grid would look
// Renders N acts (3/4/5) with a few placeholder tiles per act using SVG icons
import React, { useEffect, useMemo, useState, useContext } from "react";
import { AppContext } from "../../context/AppContext";

import iconHome from "../../assets/icons/home.svg";
import iconPlay from "../../assets/icons/play-circle.svg";
import iconPencil from "../../assets/icons/pencil.svg";
import iconUpload from "../../assets/icons/upload.svg";
import iconSliders from "../../assets/icons/sliders.svg";
import iconSparkles from "../../assets/icons/sparkles.svg";

const ICONS = [iconPlay, iconPencil, iconUpload, iconSliders, iconSparkles, iconHome];

function readStoredAct(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw !== null && raw !== '') return Number(raw);
  } catch {}

  try {
    const escapedKey = storageKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedKey}=([^;]*)`));
    if (match && match[1]) return Number(decodeURIComponent(match[1]));
  } catch {}

  return null;
}

function writeStoredAct(storageKey, act) {
  try {
    localStorage.setItem(storageKey, String(act));
  } catch {}

  try {
    document.cookie = `${storageKey}=${encodeURIComponent(String(act))}; path=/; max-age=31536000; samesite=lax`;
  } catch {}
}

function buildDummyActs(actCount = 3, tilesPerAct = 6) {
  const acts = [];
  for (let a = 1; a <= actCount; a++) {
    const tiles = [];
    for (let i = 0; i < tilesPerAct; i++) {
      const id = `${a}-${i + 1}`;
      tiles.push({
        id,
        icon: ICONS[i % ICONS.length],
        label: `Chapter ${i + 1}`,
      });
    }
    acts.push({ act: a, tiles });
  }
  return acts;
}

export default function ActIconGrid({ actCount = 3, tilesPerAct = 6, acts: actsProp, videoChapterIds, thumbByChapter, onSelect }) {
  const acts = (Array.isArray(actsProp) && actsProp.length)
    ? actsProp
    : buildDummyActs(actCount, tilesPerAct);
  const app = useContext(AppContext);
  const projectId = app?.state?.currentProject?.id || "default";
  const storageKey = useMemo(() => `lrp-active-act:${projectId}`, [projectId]);
  const actNumbers = Array.from(new Set((Array.isArray(acts) ? acts : [])
    .map((a) => Number(a.act)).filter(Number.isFinite))).sort((a, b) => a - b);
  const actSignature = actNumbers.join(',');
  const [activeAct, setActiveAct] = useState(() => null);

  // Load the active act from localStorage/cookie when the project changes.
  useEffect(() => {
    const storedAct = readStoredAct(storageKey);
    if (storedAct !== null && actNumbers.includes(storedAct)) {
      setActiveAct(storedAct);
      return;
    }

    setActiveAct(null);
  }, [storageKey, actSignature]);

  // Persist the current active act.
  useEffect(() => {
    if (activeAct === null || activeAct === undefined) return;
    writeStoredAct(storageKey, activeAct);
  }, [activeAct, storageKey]);

  const openAct = (act) => {
    setActiveAct(act);
  };

  const handleSelect = (act, tile) => {
    setActiveAct(act);
    onSelect && onSelect(tile);
  };

  return (
    <div className="lrp-act-grid" role="region" aria-label={`${actCount}-act layout preview`}>
      {acts.map(({ act, tiles, title }) => (
        <section key={act} className="lrp-act-section">
          <header className="lrp-act-header">
            <h4 className="lrp-act-title" onClick={() => openAct(act)} role="button" tabIndex={0} onKeyDown={(e)=>{ if(e.key==='Enter' || e.key===' '){ e.preventDefault(); openAct(act); }}} aria-expanded={activeAct === act} aria-controls={`act-grid-${act}`}>
              <span className={`lrp-act-chevron ${activeAct === act ? '' : 'is-collapsed'}`} aria-hidden="true">
                {/* inline chevron */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.12 9.29L12 13.17l3.88-3.88a1 1 0 1 1 1.41 1.42l-4.59 4.58a1 1 0 0 1-1.41 0L6.7 10.7a1 1 0 1 1 1.41-1.41z" fill="currentColor"/>
                </svg>
              </span>
              <span>{title || `Act ${act}`}</span>
            </h4>
            <button
              type="button"
              className="lrp-act-toggle"
              onClick={() => openAct(act)}
              aria-expanded={activeAct === act}
              aria-controls={`act-grid-${act}`}
              title={activeAct === act ? 'Open' : 'Show'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.12 9.29L12 13.17l3.88-3.88a1 1 0 1 1 1.41 1.42l-4.59 4.58a1 1 0 0 1-1.41 0L6.7 10.7a1 1 0 1 1 1.41-1.41z" fill="currentColor"/>
              </svg>
            </button>
          </header>
          {activeAct === act && (
            <div id={`act-grid-${act}`} className="lrp-icon-grid">
              {tiles.map((t, idx) => {
                const hasVideo = videoChapterIds && videoChapterIds.has?.(t.id);
                const thumbUrl = (thumbByChapter && (thumbByChapter.get?.(t.id) || thumbByChapter[t.id])) || null;
                return (
                <button
                  key={t.id}
                  type="button"
                  className={`lrp-icon-tile ${hasVideo ? 'has-video' : ''}`}
                  onClick={() => handleSelect(act, { id: t.id, act, label: t.label })}
                  aria-label={`${t.label} in Act ${act}`}
                >
                  <div className="lrp-thumb" aria-hidden="true">
                    <div
                      className={`lrp-thumb-image ${thumbUrl ? 'has-thumb' : ''}`}
                      data-variant={(idx % 6) + 1}
                      style={thumbUrl ? { backgroundImage: `url(${thumbUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                    />
                  </div>
                  <div className="lrp-icon-caption">
                    <span className="lrp-caption-title">{t.label}</span>
                    {t.readingTime ? (
                      <span className="lrp-caption-meta"> • {t.readingTime}</span>
                    ) : null}
                  </div>
                  <div className="lrp-tile-footer">
                    <button
                      type="button"
                      className="lrp-tile-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(act, { id: t.id, act, label: t.label, action: (videoChapterIds && videoChapterIds.has?.(t.id)) ? 're-read' : 'live-read' });
                      }}
                    >
                      {videoChapterIds && videoChapterIds.has?.(t.id) ? (
                        <>
                          {/* small play icon and label */}
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{marginRight:6, verticalAlign:'text-bottom'}} aria-hidden>
                            <path d="M8 5v14l11-7-11-7z"></path>
                          </svg>
                          Re‑read
                        </>
                      ) : (
                        'Live read'
                      )}
                    </button>
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
